import { useInfiniteQuery } from 'react-query';
import { GetPostsParams, PostsApi } from 'app/modules/posts/api/posts';
import { useMemo } from 'react';
import useInfiniteScroll from 'utils/hooks/useInfiniteScroll';
import { queryKey } from 'constants/queryKey';

export function useApprovalsQueries(params: Omit<GetPostsParams, 'page'>) {
  const isQueryEnabled =
    (params.profiles && params.profiles.length > 0) ||
    (params.profileGroups && params.profileGroups.length > 0);

  const postsQueryData = useInfiniteQuery(
    queryKey.approvalsPosts(params),
    ({ pageParam = 1 }) => {
      return PostsApi.posts({
        ...params,
        page: pageParam,
        persistFilters: false,
      });
    },
    {
      refetchOnWindowFocus: false,
      refetchInterval: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      refetchIntervalInBackground: false,
      enabled: isQueryEnabled,
      getNextPageParam: (lastPage, allPages) => {
        const totalCount = lastPage.pagination?.totalCount ?? 0;
        const limit = params.limit ?? 10;
        const totalPages = Math.ceil(totalCount / limit);
        const currentPage = allPages.length;

        return currentPage < totalPages ? currentPage + 1 : undefined;
      },
    },
  );

  const posts = useMemo(() => {
    return postsQueryData.data?.pages.flatMap((page) => page.data) ?? [];
  }, [postsQueryData.data]);

  const [sentryRef, { rootRef }] = useInfiniteScroll({
    loading: postsQueryData.isLoading || postsQueryData.isFetchingNextPage,
    hasNextPage: postsQueryData.hasNextPage ?? false,
    onLoadMore: () => {
      postsQueryData.fetchNextPage();
    },
  });

  return {
    posts,
    isFetching: postsQueryData.isFetching,
    isLoading: postsQueryData.isLoading,
    isFetchingNextPage: postsQueryData.isFetchingNextPage,
    hasNextPage: postsQueryData.hasNextPage ?? false,
    error: postsQueryData.error,
    refetch: postsQueryData.refetch,
    sentryRef,
    rootRef,
  };
}
